
import React from 'react';
import { Link } from 'react-router-dom';
import { Target, BarChart2, ArrowRight } from 'lucide-react';
import FadeIn from '../../../components/FadeIn';
import SEO from '../../../components/SEO';

const LeadScoring = () => {
    return (
        <div className="bg-white">
            <SEO
                title="Automated Lead Scoring Training | BPO Consultants"
                description="Learn to use predictive analytics to qualify leads. Master data enrichment and intent data for higher conversion rates."
                keywords="Lead Scoring Course, Predictive Data Analytics, Sales Qualification Training, B2B Intent Data, Sales Operations"
            />

            {/* Hero */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#0B0D15] overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200" alt="Data Analytics" className="w-full h-full object-cover" />
                </div>
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <span className="inline-block bg-[#A2CB52]/20 text-[#A2CB52] px-4 py-2 rounded-full text-sm font-bold border border-[#A2CB52]/40 mb-6 backdrop-blur-sm">
                            <Target size={14} className="inline mr-1" /> Module 2: Analytics
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            Automated Lead Scoring <br /> & Qualification
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Stop wasting time on bad leads. Use data to predict who is ready to buy.
                        </p>
                    </FadeIn>
                </div>
            </section>

            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <FadeIn className="mb-16">
                        <h2 className="text-3xl font-bold text-[#20222D] mb-6">The Science of Qualification</h2>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                            In this module, we move away from "gut feel" and towards data-driven precision. Learn how to set up scoring models that automatically rank your prospects based on demographic fit and behavioral signals.
                        </p>

                        <div className="grid md:grid-cols-2 gap-8">
                            {[
                                {
                                    title: "Firmographic Data",
                                    desc: "Enriching lead profiles with company size, revenue, tech stack, and funding data automatically."
                                },
                                {
                                    title: "Behavioral Signals",
                                    desc: "Tracking website visits, email opens, and content downloads to gauge interest levels."
                                },
                                {
                                    title: "Intent Data",
                                    desc: "Leveraging 3rd party signals (like G2 reviews or hiring trends) to identify active buyers."
                                },
                                {
                                    title: "Tiered Account Strategy",
                                    desc: "Building a Tier 1, 2, and 3 strategy to allocate your time effectively."
                                }
                            ].map((item, i) => (
                                <div key={i} className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                                    <div className="text-[#A2CB52] mb-4">
                                        <BarChart2 size={28} />
                                    </div>
                                    <h3 className="text-xl font-bold text-[#20222D] mb-2">{item.title}</h3>
                                    <p className="text-gray-600 text-sm">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </FadeIn>

                    {/* SGE / FAQ Section */}
                    <FadeIn className="mb-16">
                        <h2 className="text-2xl font-bold text-[#20222D] mb-6">Why This Matters</h2>
                        <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                            <p className="text-blue-900 font-medium italic">
                                "Companies that excel at lead nurturing generate 50% more sales leads at 33% lower cost."
                            </p>
                            <p className="text-blue-700 text-sm mt-2">- Forrester Research</p>
                        </div>
                        <div className="mt-8 space-y-4">
                            <div className="border-b border-gray-200 pb-4">
                                <h3 className="font-bold text-gray-900 mb-2">What tools do you teach?</h3>
                                <p className="text-gray-600">We cover Salesforce IQ, HubSpot Scoring, Apollo.io, and 6sense.</p>
                            </div>
                        </div>
                    </FadeIn>

                    <FadeIn className="bg-[#20222D] rounded-3xl p-12 text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-[#A2CB52] blur-[100px] opacity-20 pointer-events-none"></div>
                        <h2 className="text-3xl font-bold text-white mb-6">Get Certified</h2>
                        <Link to="/contact" className="inline-flex items-center bg-[#A2CB52] text-[#20222D] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#B5D575] transition-all transform hover:scale-105">
                            Start Module <ArrowRight className="ml-2" size={20} />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
};

export default LeadScoring;
